import { Box } from "@chakra-ui/react";
import { MessageThreadFragment } from "gql/graphql";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";

import DistributionGroupTooltip from "modules/_shared/distribution-group-tooltip";
import Pill from "modules/_shared/pill";
import React, { memo, useCallback, useMemo } from "react";
import { CALLOUT_KEY } from "modules/utils/Constants";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { useAuth } from "modules/providers/auth-provider";
import useRemovedFromThread from "modules/hooks/use-removed-from-thread";
import {
  isMessageThreadReadOnly,
  removedUsersForGroup,
} from "modules/utils/Functions";

const DistributionGroupActionablePill: React.FC<{
  messageThread: MessageThreadFragment;
  distributionGroupId: string;
}> = ({ messageThread, distributionGroupId }) => {
  const { me } = useAuth();
  const { distributionGroupsById } = useDistributionGroups();
  const { openModal } = useOverlay();
  const distributionGroup = distributionGroupsById[distributionGroupId];
  const onRemoveDistributionGroupClicked = useCallback(
    (distributionGroupId: string) => () => {
      openModal({
        type: MODAL_TYPE.CONFIRM_REMOVE_USERS_GROUPS,
        id: messageThread.id,
        ids: [distributionGroupId],
      });
    },
    [messageThread.id, openModal],
  );

  const { isMeRemoved } = useRemovedFromThread(messageThread);

  const removedUserList = messageThread.removedUserList;

  const canRemoveDistributionGroup = useMemo(() => {
    if (!me) return false;
    if (isMeRemoved) return false;
    if (messageThread.isLocked) return false;
    if (
      isMessageThreadReadOnly(messageThread) &&
      !messageThread.createMessageUserIdList.includes(me.id)
    ) {
      return false;
    }
    return true;
  }, [isMeRemoved, me, messageThread]);

  if (!distributionGroup) return null;

  const removedUsers = removedUsersForGroup(distributionGroup, removedUserList);

  return (
    <DistributionGroupTooltip
      id={distributionGroup.id}
      accessUserIds={messageThread.accessUserIdList}
      removedUserList={removedUserList}
    >
      <Box>
        <Pill
          id={distributionGroup.id}
          label={distributionGroup.name}
          isActive={distributionGroup.active}
          leftChar={CALLOUT_KEY}
          isModified={removedUsers.length > 0}
          onClose={
            canRemoveDistributionGroup
              ? onRemoveDistributionGroupClicked(distributionGroup.id)
              : undefined
          }
        />
      </Box>
    </DistributionGroupTooltip>
  );
};

export default memo(DistributionGroupActionablePill);
